'use client'

import { motion } from 'framer-motion'
import { Target, Book, Key, Brain } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
}

type BenefitCardProps = {
  Icon: LucideIcon
  title: string
  description: string
}

const BenefitCard: React.FC<BenefitCardProps> = ({
  Icon,
  title,
  description,
}) => (
  <motion.div variants={fadeIn} className="h-full">
    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md ring-1 ring-border transition-transform will-change-transform hover:-translate-y-2 h-full">
      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
        {title}
      </h3>
      <p className="text-sm sm:text-base text-gray-700">{description}</p>
    </div>
  </motion.div>
)

const benefits: BenefitCardProps[] = [
  {
    Icon: Target,
    title: 'Personalised & Effective Treatment',
    description:
      'An accurate diagnosis ensures your treatment plan is precisely tailored, significantly increasing the likelihood of positive outcomes and lasting relief.',
  },
  {
    Icon: Book,
    title: 'Empowerment Through Understanding',
    description:
      'Knowing your diagnosis can demystify your experiences, reduce self-blame, and empower you to actively participate in your recovery journey.',
  },
  {
    Icon: Key,
    title: 'Access to Tailored Support',
    description:
      'A clear diagnosis helps connect you with the most appropriate therapies, specialised resources, and support systems for your specific condition.',
  },
  {
    Icon: Brain,
    title: 'Reduced Stigma & Increased Hope',
    description:
      'Naming and understanding your experience can reduce feelings of isolation and provide a clear path forward with renewed hope.',
  },
]

const PsychiatricEvaluationsBenefits: React.FC = () => (
  <section className="py-12 sm:py-16 lg:py-20 px-4">
    <div className="max-w-7xl mx-auto">
      <motion.div
        className="text-center mb-10 sm:mb-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={fadeIn}
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-gray-900 mb-4">
          The Benefits of a Comprehensive Evaluation
        </h2>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerContainer}
      >
        {benefits.map((benefit) => (
          <BenefitCard key={benefit.title} {...benefit} />
        ))}
      </motion.div>
    </div>
  </section>
)

export default PsychiatricEvaluationsBenefits
